import _get from 'lodash/get';
import { ApolloCache } from '@apollo/client/cache';

import { addTypenamesToData } from '../utils';
import { GET_PRO_INGESTIONS } from './proIngestion';
import { GET_STATEMENTS_UI_STATE } from '../uiState/getStatementsUiState';

export const addTypenamesToDashboard = (
    dashboard: ProIngestionDashboard,
): ProIngestionDashboard => {
    const typenameMaps = [
        {
            path: ['chart_tracks'],
            typename: 'PROChartTrackWrapper',
        },
        {
            path: ['chart_tracks', 'digital'],
            typename: 'PROChartTrack',
        },
        {
            path: [
                'chart_tracks',
                'digital',
                'music_user_genre_name_contributions',
            ],
            typename: 'PROMusicUserGenreNameContribution',
        },
        {
            path: ['chart_tracks', 'nondigital'],
            typename: 'PROChartTrack',
        },
        {
            path: [
                'chart_tracks',
                'nondigital',
                'music_user_genre_name_contributions',
            ],
            typename: 'PROMusicUserGenreNameContribution',
        },
        {
            path: ['chart_parameters'],
            typename: 'PROChartParameter',
        },
        {
            path: ['country_codes'],
            typename: 'PROCountryCode',
        },
        {
            path: ['kpis'],
            typename: 'PROKpis',
        },
        {
            path: ['music_user_genres'],
            typename: 'PROMusicUserGenre',
        },
        {
            path: ['directed_tree'],
            typename: 'PRODirectedTreeNode',
            recursionKey: 'children',
        },
        {
            path: ['shows'],
            typename: 'PROShow',
        },
        {
            path: ['sources'],
            typename: 'PROSource',
        },
        {
            path: ['statement_info'],
            typename: 'PROStatementInfo',
        },
        {
            path: ['statement_info', 'period'],
            typename: 'PROStatementPeriod',
        },
        {
            path: ['use_codes'],
            typename: 'PROUseCode',
        },
    ];

    addTypenamesToData(dashboard, typenameMaps);

    // eslint-disable-next-line no-param-reassign
    dashboard['__typename'] = 'PROIngestionDashboard';

    return dashboard;
};

const addNullValuesToDashboard = (
    dashboard: ProIngestionDashboard,
): ProIngestionDashboard => {
    /* eslint-disable no-param-reassign */

    // dashboard.tracks will be null on the initial return from proNormalize
    if (dashboard.tracks !== null && dashboard.tracks !== undefined) {
        dashboard.tracks.forEach((track: Maybe<ProTrack>): void => {
            if (track) {
                if (track.composers === undefined) {
                    track.composers = null;
                }
                if (track.publishers === undefined) {
                    track.publishers = null;
                }
                if (track.subpublishers === undefined) {
                    track.subpublishers = null;
                }
            }
        });
    }

    if (dashboard.chart_tracks === undefined) {
        dashboard.chart_tracks = null;
    }

    /* eslint-enable no-param-reassign */

    return dashboard;
};

// Add `__typename`s to the PRO ingestion data and add the data to the cache.
export const handleProIngestion = (
    statusCode: number,
    proIngestionDashboard: ProIngestionDashboard,
    filename: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
): ProIngestionDashboard => {
    const statementId = _get(proIngestionDashboard, [
        'statement_info',
        'statement_id',
    ]);
    const dashboard = addNullValuesToDashboard(
        addTypenamesToDashboard(proIngestionDashboard),
    );

    const newIngestion = {
        // Add an `id` property so that Apollo Client can use it along with the __typename to create
        // a cacheKey
        id: statementId,
        data: {
            dashboard,
            status_code: statusCode,
            __typename: 'PROIngestionData',
        },
        filename,
        statementId,
        __typename: 'PROIngestion',
    };

    // Get current `proIngestions` list. If a proIngestion with a matching statementId
    // already exists, replace it with the new ingestion. If not, add the new ingestion
    // to the list.
    const cacheQuery = cache.readQuery({
        query: GET_PRO_INGESTIONS,
    });

    const proIngestions = _get(cacheQuery, ['proIngestions'], null) || [];
    const nextProIngestions = [...proIngestions];

    let didReplaceIngestion = false;

    for (let i = 0; i < nextProIngestions.length; i += 1) {
        const item = nextProIngestions[i];

        if (item.statementId === statementId) {
            nextProIngestions[i] = newIngestion;
            didReplaceIngestion = true;
            break;
        }
    }

    if (!didReplaceIngestion) {
        nextProIngestions.push(newIngestion);
    }

    cache.writeQuery({
        query: GET_PRO_INGESTIONS,
        data: {
            proIngestions: nextProIngestions,
        },
    });

    // Set the UI to use this ingestion as the current one
    cache.writeQuery({
        query: GET_STATEMENTS_UI_STATE,
        data: {
            uiState: {
                statements: {
                    statementId,
                    __typename: 'Statements',
                },
                __typename: 'UIState',
            },
        },
    });

    return proIngestionDashboard;
};
